import React from 'react';
import { formatEnumeratedListBreaks } from './formatReadingText';

type ReadingBlock = { type: 'h3' | 'p'; text: string };

function parseInlineMarkdownSegmentsSimple(source: string): Array<{ bold: boolean; text: string }> {
  const segments: Array<{ bold: boolean; text: string }> = [];
  const matches = [...source.matchAll(/\*\*([^*]+)\*\*/g)];
  let cursor = 0;

  for (const match of matches) {
    const start = match.index ?? 0;
    if (start > cursor) {
      segments.push({ bold: false, text: source.slice(cursor, start) });
    }
    segments.push({ bold: true, text: match[1] });
    cursor = start + match[0].length;
  }

  if (cursor < source.length) {
    segments.push({ bold: false, text: source.slice(cursor) });
  }

  return segments.length ? segments : [{ bold: false, text: source }];
}

/** Segmentos de texto plano o negrita; tolera `**` huérfanos sin romper el párrafo. */
export function parseInlineMarkdownSegments(text: string): Array<{ bold: boolean; text: string }> {
  const original = String(text || '');
  const allMarkers: number[] = [];
  for (let index = 0; index < original.length - 1; index += 1) {
    if (original.startsWith('**', index)) {
      allMarkers.push(index);
      index += 1;
    }
  }

  const hasLeadingOrphanPair =
    allMarkers.length >= 3 &&
    allMarkers[0] === 0 &&
    original.slice(2, allMarkers[1]).includes(' ');

  if (hasLeadingOrphanPair) {
    const plain = original.slice(0, allMarkers[1]);
    const boldSection = original.slice(allMarkers[1], allMarkers[2] + 2);
    const tail = original.slice(allMarkers[2] + 2);
    const result = [{ bold: false, text: plain }, ...parseInlineMarkdownSegmentsSimple(boldSection)];
    if (tail) {
      result.push(...parseInlineMarkdownSegments(tail));
    }
    return result;
  }

  let source = original;
  if (allMarkers.length % 2 !== 0) {
    const last = allMarkers[allMarkers.length - 1];
    source = `${source.slice(0, last)}${source.slice(last + 2)}`;
  }

  return parseInlineMarkdownSegmentsSimple(source);
}

function renderInlineMarkdown(text: string): React.ReactNode[] {
  return parseInlineMarkdownSegments(text).map((segment, index) =>
    segment.bold ? (
      <strong key={index}>{segment.text}</strong>
    ) : (
      <React.Fragment key={index}>{segment.text}</React.Fragment>
    ),
  );
}

export function parseReadingBlocks(text: string): ReadingBlock[] {
  const normalized = formatEnumeratedListBreaks(text);
  const lines = normalized.split('\n');
  const blocks: ReadingBlock[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    const joined = paragraph.join('\n').trim();
    if (joined) blocks.push({ type: 'p', text: joined });
    paragraph = [];
  };

  for (const line of lines) {
    const h3 = line.match(/^###\s+(.+)$/);
    if (h3) {
      flushParagraph();
      blocks.push({ type: 'h3', text: h3[1].trim() });
      continue;
    }
    if (!line.trim() && paragraph.length) {
      flushParagraph();
      continue;
    }
    paragraph.push(line);
  }

  flushParagraph();
  return blocks.length ? blocks : [{ type: 'p', text: normalized }];
}

/** Renderiza bodyText con ### → h3, **negrita** y párrafos. */
export function ReadingRichText({ text, className }: { text: string; className?: string }) {
  const blocks = parseReadingBlocks(text);

  return (
    <div className={['reading-rich-text', className].filter(Boolean).join(' ')}>
      {blocks.map((block, index) =>
        block.type === 'h3' ? (
          <h3 key={index} className="reading-rich-text__h3">
            {renderInlineMarkdown(block.text)}
          </h3>
        ) : (
          <p key={index} className="reading-rich-text__p whitespace-pre-wrap">
            {renderInlineMarkdown(block.text)}
          </p>
        ),
      )}
    </div>
  );
}
